import { Button } from '@consigli/facade';
import {
  useBuildingPackageId,
  useProjectId,
  useLazyGetBuildingPackageBuildingJsonQuery,
  useUpdateBuildingPackageBuildingJsonMutation,
} from '@consigli/hooks';
import { type BuildingJSONData } from '@consigli/types';
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSettings, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';

import { mapRoomsFromBuildingJson, mapRoomCriteriaToBuilding } from './building-json-mapper';
import {
  type RoomCriteriaData,
  type ColumnConfig,
  getColumnConfigs,
  ALWAYS_VISIBLE_COLUMNS,
} from './room-criteria-config';

import type { BuildingJsonData } from './types';

interface RoomLevelDimensioningCriteriaProps {
  onApprove?: (data: RoomCriteriaData[]) => void;
}

export const RoomLevelDimensioningCriteria: React.FC<RoomLevelDimensioningCriteriaProps> = ({
  onApprove,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const buildingPackageId = useBuildingPackageId();
  const [getBuildingJson] = useLazyGetBuildingPackageBuildingJsonQuery();
  const [updateBuildingPackageBuildingJson] = useUpdateBuildingPackageBuildingJsonMutation();

  // Building JSON state
  const [buildingJson, setBuildingJson] = useState<BuildingJSONData | null>(null);
  const [fullBuildingData, setFullBuildingData] = useState<BuildingJsonData | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Map building JSON to room criteria data
  const roomsFromBuildingJson = useMemo(() => {
    if (!buildingJson) return [];
    try {
      const buildingData: BuildingJsonData = buildingJson as BuildingJsonData;
      return mapRoomsFromBuildingJson(buildingData);
    } catch (error) {
      console.error('Error parsing building JSON:', error);
      return [];
    }
  }, [buildingJson]);

  const [data, setData] = useState<RoomCriteriaData[]>(roomsFromBuildingJson);
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [columns, setColumns] = useState<ColumnConfig[]>(() => getColumnConfigs(t));

  // Fetch building JSON function
  const fetchBuildingJson = useCallback(async () => {
    try {
      setIsLoadingData(true);
      const result = await getBuildingJson({
        projectId,
        buildingPackageId,
      }).unwrap();
      setBuildingJson(result);

      if (result) {
        const parsed: BuildingJsonData = result as BuildingJsonData;
        setFullBuildingData(parsed);
      }

      return result;
    } catch (error) {
      console.error('Failed to fetch building JSON:', error);
      return null;
    } finally {
      setIsLoadingData(false);
    }
  }, [getBuildingJson, projectId, buildingPackageId]);

  // Fetch building JSON on mount
  useEffect(() => {
    if (projectId && buildingPackageId) {
      fetchBuildingJson();
    }
  }, [fetchBuildingJson, projectId, buildingPackageId]);

  useEffect(() => {
    if (roomsFromBuildingJson.length > 0) {
      setData(roomsFromBuildingJson);
    }
  }, [roomsFromBuildingJson]);

  // Update column labels when language changes
  useEffect(() => {
    setColumns(getColumnConfigs(t));
  }, [t]);

  const visibleColumns = useMemo(() => columns.filter((col) => col.visible), [columns]);

  const toggleableColumns = useMemo(
    () =>
      columns.filter(
        (col) =>
          !ALWAYS_VISIBLE_COLUMNS.includes(col.key as (typeof ALWAYS_VISIBLE_COLUMNS)[number]),
      ),
    [columns],
  );

  // Auto-focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Close dropdown when clicking outside (but not when clicking the button)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const withinContainer = containerRef.current?.contains(target);
      const withinDropdown = dropdownRef.current?.contains(target);

      // If click is outside the container (button + dropdown), close it
      if (!withinContainer && !withinDropdown) {
        setShowColumnToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getApproveError = useCallback((err: unknown): string => {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Unknown error';
    }
  }, []);

  const handleApprove = useCallback(async () => {
    try {
      setApproveError(null);
      toast.success(t('room-criteria.approved-success'));
      if (onApprove) {
        await onApprove(data);
      }
    } catch (e) {
      console.error('Error saving room criteria:', e);
      setApproveError(getApproveError(e));
      toast.error(t('room-criteria.error-approving'));
    }
  }, [data, onApprove, t, getApproveError]);

  const editableFieldKeys = useMemo(
    () => new Set(columns.filter((c) => c.editable).map((c) => c.key)),
    [columns],
  );

  const getCellClassName = useCallback(
    (value: string | number | boolean, field: ColumnConfig['key'], rowData: RoomCriteriaData) => {
      const isEditable = editableFieldKeys.has(field);
      let baseClasses = 'px-3 py-2 text-sm border-r border-gray-200';

      if (!isEditable) {
        return baseClasses + ' text-gray-700';
      }

      const source = (rowData.valueSource?.[field as keyof typeof rowData.valueSource] ??
        'default') as 'specification' | 'default' | 'user';
      baseClasses += ' cursor-pointer hover:bg-gray-50';

      if (source === 'specification') {
        baseClasses += ' text-blue-600 font-medium';
      } else if (source === 'default') {
        baseClasses += ' text-green-600 font-medium';
      } else if (source === 'user') {
        baseClasses += ' text-purple-600 font-medium';
      }

      return baseClasses;
    },
    [editableFieldKeys],
  );

  const handleCellEdit = useCallback(
    async (rowId: string, field: string, value: string | number | boolean) => {
      // Update local state first
      const updatedData = data.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
              valueSource: {
                ...row.valueSource,
                [field]: 'user',
              },
            }
          : row,
      );

      setData(updatedData);
      setEditingCell(null);

      // Force blur to ensure input loses focus
      if (inputRef.current) {
        inputRef.current.blur();
      }

      // Auto-save the changes to building JSON
      try {
        const updatedBuildingJson = fullBuildingData
          ? mapRoomCriteriaToBuilding(updatedData, fullBuildingData)
          : null;
        if (updatedBuildingJson) {
          await updateBuildingPackageBuildingJson({
            projectId,
            buildingPackageId,
            data: updatedBuildingJson,
          });

          // Update local state with the new building data
          setFullBuildingData(updatedBuildingJson);
        }
      } catch (error) {
        console.error('Error auto-saving room criteria:', error);
        // Don't show error toast for auto-save failures to avoid being intrusive
      }
    },
    [data, fullBuildingData, updateBuildingPackageBuildingJson, projectId, buildingPackageId],
  );

  const handleCellClick = useCallback((rowId: string, field: string, editable: boolean) => {
    if (editable) {
      setEditingCell({ rowId, field });
    }
  }, []);

  const toggleColumn = useCallback((columnKey: string) => {
    // Prevent toggling essential columns that should always be visible
    if (ALWAYS_VISIBLE_COLUMNS.includes(columnKey as (typeof ALWAYS_VISIBLE_COLUMNS)[number])) {
      return;
    }

    setColumns((prevColumns) =>
      prevColumns.map((col) => (col.key === columnKey ? { ...col, visible: !col.visible } : col)),
    );
  }, []);

  // Memoize the column toggle button click handler
  const handleColumnToggleClick = useCallback(() => {
    setShowColumnToggle((prev) => !prev);
  }, []);

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <div className="p-12">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('room-criteria.title')}</h1>
          <p className="text-gray-600 mb-6">{t('room-criteria.description')}</p>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('room-criteria.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-12">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('room-criteria.title')}</h1>
        <p className="text-gray-600 mb-6">{t('room-criteria.description')}</p>
      </div>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('room-criteria.section-title')}</h2>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600">
          {t('room-criteria.showing-count', {
            visible: visibleColumns.length,
            total: columns.length,
          })}
        </div>
        <div className="relative" ref={containerRef}>
          <Button
            onClick={handleColumnToggleClick}
            secondary
            rounded
            icon={FiSettings}
            className="text-sm"
          >
            {t('room-criteria.columns-toggle', {
              count: toggleableColumns.length,
            })}
          </Button>

          {showColumnToggle && (
            <div
              ref={dropdownRef}
              className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-4"
            >
              <h3 className="font-medium text-gray-900 mb-3">
                {t('room-criteria.toggle-columns')}
              </h3>
              <p className="text-xs text-gray-500 mb-3">{t('room-criteria.always-visible-note')}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {toggleableColumns.map((column) => (
                  <label key={column.key} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={column.visible}
                      onChange={() => toggleColumn(column.key)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-800">{column.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-6 text-sm mt-6 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-600 rounded"></div>
          <span className="text-gray-600">{t('room-criteria.legend.specification')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-600 rounded"></div>
          <span className="text-gray-600">{t('room-criteria.legend.default')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-purple-600 rounded"></div>
          <span className="text-gray-600">{t('room-criteria.legend.user-modified')}</span>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="px-3 py-3 text-left text-sm font-medium text-gray-700 border-r border-gray-200 min-w-[120px]"
                >
                  <div>
                    {column.label}
                    {column.unit && (
                      <div className="text-gray-500 font-normal text-xs">({column.unit})</div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {visibleColumns.map((column) => {
                  const value = row[column.key];
                  const isEditing =
                    editingCell?.rowId === row.id && editingCell?.field === column.key;

                  return (
                    <td
                      key={column.key}
                      className={getCellClassName(value, column.key, row)}
                      onClick={() => handleCellClick(row.id, column.key, column.editable)}
                    >
                      {isEditing ? (
                        column.type === 'checkbox' ? (
                          <input
                            ref={inputRef}
                            type="checkbox"
                            defaultChecked={Boolean(value)}
                            onChange={(e) => {
                              handleCellEdit(row.id, column.key, e.target.checked);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Escape') {
                                const newValue =
                                  e.key === 'Escape' ? Boolean(value) : e.currentTarget.checked;
                                handleCellEdit(row.id, column.key, newValue);
                              }
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                        ) : (
                          <input
                            ref={inputRef}
                            type={column.type}
                            defaultValue={value?.toString() || ''}
                            onBlur={(e) => {
                              e.preventDefault();
                              const newValue =
                                column.type === 'number'
                                  ? parseFloat(e.target.value) || 0
                                  : e.target.value;
                              handleCellEdit(row.id, column.key, newValue);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Escape') {
                                const newValue =
                                  e.key === 'Escape'
                                    ? value // Revert to original value on Escape
                                    : column.type === 'number'
                                      ? parseFloat(e.currentTarget.value) || 0
                                      : e.currentTarget.value;
                                handleCellEdit(row.id, column.key, newValue);
                              }
                            }}
                            className="w-full h-6 px-1 text-sm bg-white border border-blue-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            style={{
                              minWidth: '60px',
                              maxWidth: '100%',
                              fontSize: 'inherit',
                              lineHeight: 'inherit',
                            }}
                          />
                        )
                      ) : (
                        <span className="block min-h-[24px]">
                          {column.type === 'checkbox' ? (value ? 'Yes' : 'No') : value?.toString()}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-xs ml-auto mt-4">
          <Button
            onClick={handleApprove}
            primary
            rounded
            icon={FiCheck}
            className="w-full px-6 py-3"
          >
            {t('room-criteria.approve')}
          </Button>
        </div>
      </div>
      {approveError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm font-medium text-red-800">
            {t('room-criteria.error-approving')} {approveError}
          </p>
        </div>
      )}
    </div>
  );
};
